import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  headerCellStyle,
  cellStyle,
} from "../../components/tableHelpers/TableStyleUtils";
import "./TopsTables.css";

//region Helpers

/**
 * Reads UnitPrice from a quote in a safe/normalized way.
 *
 * Important:
 * - the source top references are now UnitPrice-based;
 * - therefore this table must compare quotes using UnitPrice;
 * - BidValue is no longer the comparison source here.
 *
 * @param {object} quote - TenderQuote row.
 * @returns {number|null} Normalized UnitPrice or null.
 */
const getQuoteUnitPrice = (quote) => {
  const value = Number(quote?.UnitPrice ?? quote?.unitPrice);
  return Number.isFinite(value) ? value : null;
};

//endregion

export default function TopsBySourceTable({ model, top }) {
  const { t } = useTranslation();

  //region Prepare the data for the table

  /**
   * Builds the source statistics table.
   *
   * Semantics:
   * - top=1 => compare against source top1 UnitPrice
   * - top=2 => compare against source topN UnitPrice
   *
   * Matching rule:
   * - lower UnitPrice is better;
   * - a quote matches when quote.UnitPrice <= reference UnitPrice.
   */
  const tableData = useMemo(() => {
    const sources = Array.from(model.quotesBySource.keys());

    const stats = new Map();

    (model.normCarriers || []).forEach((carrier) => {
      stats.set(carrier.id, {
        name: carrier.name,
        perSource: Object.fromEntries(sources.map((source) => [source, "0%"])),
        total: 0,
      });
    });

    sources.forEach((source) => {
      const sourceQuotes = model.quotesBySource.get(source) || [];
      const ref = model.topBySource.get(source) || { top1: null, topN: null };
      const refValue = top === 1 ? ref.top1 : ref.topN;

      const byCarrier = new Map();

      sourceQuotes.forEach((quote) => {
        const carrierId = quote.CarrierId ?? quote.Carrier?.Id;
        if (carrierId == null) return;

        const arr = byCarrier.get(carrierId) || [];
        arr.push(quote);
        byCarrier.set(carrierId, arr);
      });

      byCarrier.forEach((arr, carrierId) => {
        if (!stats.has(carrierId)) {
          stats.set(carrierId, {
            name: arr[0]?.CarrierName ?? String(carrierId),
            perSource: Object.fromEntries(sources.map((s) => [s, "0%"])),
            total: 0,
          });
        }

        const matching =
          refValue != null
            ? arr.filter((quote) => {
              const unitPrice = getQuoteUnitPrice(quote);
              return unitPrice != null && unitPrice <= Number(refValue);
            }).length
            : 0;

        const pct = arr.length ? Math.round((matching / arr.length) * 100) : 0;

        const s = stats.get(carrierId);
        s.perSource[source] = `${pct}%`;
        s.total += matching;
      });
    });

    return {
      sources,
      rows: (model.normCarriers || []).map((carrier) => {
        const s = stats.get(carrier.id) ?? {
          name: carrier.name,
          perSource: Object.fromEntries(sources.map((source) => [source, "0%"])),
          total: 0,
        };

        return {
          name: s.name,
          values: s.perSource,
          total: s.total,
        };
      }),
    };
  }, [model, top]);

  //endregion

  //region Render

  return (
    <div className="tops-table-wrapper" style={{ marginTop: "40px" }}>
      <span
        style={{
          fontWeight: "bold",
          color: "var(--color-red-dark)",
          fontSize: "16px",
          display: "flex",
          marginBottom: "10px",
          borderBottom: "2px solid var(--color-red-dark)",
        }}
      >
        {t("TopSource", { top })}
      </span>

      <div className="tops-table-container">
        <table className="tops-table tops-table-source">
          <thead>
            <tr>
              <th style={headerCellStyle()} className="tops-col-carrier">
                {t("Carrier")}
              </th>
              {tableData.sources.map((source) => (
                <th
                  key={source}
                  style={headerCellStyle()}
                  className="tops-col-main"
                >
                  {t(source)}
                </th>
              ))}
              <th style={headerCellStyle()} className="tops-col-total">
                {t("Total")}
              </th>
            </tr>
          </thead>
          <tbody>
            {tableData.rows.map((row, idx) => (
              <tr key={idx}>
                <td
                  style={cellStyle({ background: "var(--color-white)" })}
                  className="tops-col-carrier tops-cell"
                >
                  {row.name}
                </td>
                {tableData.sources.map((source) => (
                  <td
                    key={source}
                    style={cellStyle({ background: "var(--color-white)" })}
                    className="tops-col-main tops-cell"
                  >
                    {row.values[source] || "0%"}
                  </td>
                ))}
                <td
                  style={cellStyle({ background: "var(--color-white)" })}
                  className="tops-col-total tops-cell"
                >
                  {row.total}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  //endregion
}

TopsBySourceTable.propTypes = {
  model: PropTypes.shape({
    normCarriers: PropTypes.array.isRequired,
    quotesBySource: PropTypes.instanceOf(Map).isRequired,
    topBySource: PropTypes.instanceOf(Map).isRequired,
  }).isRequired,
  top: PropTypes.oneOf([1, 2]).isRequired,
};